import React, { useState, useEffect, useCallback } from "react";
import { Save, Image } from "lucide-react";
import { supabase } from "../../lib/supabase";
import ImageSelector from "../ImageSelector";

interface MediaSectionProps {
  courseId?: string;
  initialData: any;
}

export default function MediaSection({
  courseId,
  initialData,
}: MediaSectionProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showImageSelector, setShowImageSelector] = useState(false);
  const [imageType, setImageType] = useState<"featured" | "thumbnail">(
    "featured",
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formData, setFormData] = useState({
    image_url: initialData?.image_url || "",
    thumbnail_url: initialData?.thumbnail_url || "",
  });

  const checkForChanges = useCallback(() => {
    const hasChanges =
      formData.image_url !== (initialData?.image_url || "") ||
      formData.thumbnail_url !== (initialData?.thumbnail_url || "");
    setHasUnsavedChanges(hasChanges);
  }, [formData, initialData]);

  useEffect(() => {
    checkForChanges();
  }, [formData, checkForChanges]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!courseId) {
        throw new Error("Course ID is required");
      }

      const { error } = await supabase
        .from("courses")
        .update({
          image_url: formData.image_url,
          thumbnail_url: formData.thumbnail_url || null,
        })
        .eq("id", courseId);

      if (error) throw error;

      // Update initialData reference after successful save
      Object.assign(initialData, formData);
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error saving media:", err);
      setError(err instanceof Error ? err.message : "Failed to save media");
    } finally {
      setSaving(false);
    }
  };

  const handleImageSelect = (url: string) => {
    setFormData((prev) => ({
      ...prev,
      [imageType === "featured" ? "image_url" : "thumbnail_url"]: url,
    }));
    setShowImageSelector(false);
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-gray-900">Media</h2>
          <button
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${
              hasUnsavedChanges
                ? "text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                : "text-gray-500 bg-gray-100"
            } disabled:opacity-50 transition-colors duration-200`}
          >
            <Save className="h-5 w-5 mr-2" />
            {saving ? "Saving..." : "Save Media"}
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Featured Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Featured Image
            </label>
            <div className="flex items-start space-x-4">
              <div className="flex-grow">
                <input
                  type="text"
                  value={formData.image_url}
                  onChange={(e) =>
                    setFormData({ ...formData, image_url: e.target.value })
                  }
                  placeholder="Image URL"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setImageType("featured");
                  setShowImageSelector(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Image className="h-5 w-5 mr-2" />
                Browse
              </button>
            </div>
            {formData.image_url && (
              <div className="mt-2">
                <img
                  src={formData.image_url}
                  alt="Featured Preview"
                  className="h-32 w-auto object-cover rounded-lg"
                />
              </div>
            )}
          </div>

          {/* Thumbnail Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Thumbnail Image (Optional)
            </label>
            <div className="flex items-start space-x-4">
              <div className="flex-grow">
                <input
                  type="text"
                  value={formData.thumbnail_url}
                  onChange={(e) =>
                    setFormData({ ...formData, thumbnail_url: e.target.value })
                  }
                  placeholder="Thumbnail URL"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setImageType("thumbnail");
                  setShowImageSelector(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Image className="h-5 w-5 mr-2" />
                Browse
              </button>
            </div>
            {formData.thumbnail_url && (
              <div className="mt-2">
                <img
                  src={formData.thumbnail_url}
                  alt="Thumbnail Preview"
                  className="h-32 w-auto object-cover rounded-lg"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <ImageSelector
        isOpen={showImageSelector}
        onClose={() => setShowImageSelector(false)}
        onSelect={handleImageSelect}
      />
    </div>
  );
}
